<template>
	<div class="scrolling-wrapper" v-resize.initial="handleResize">
		<div v-if="!$isMobile"
			class="scroll-arrow left"
			@mousedown="() => handleArrowClick(-360)">
			<BaseIcon
				:isCustom="true"
				icon="backChevron"
				size="3x"/>
		</div>
		<div ref="scrollingContainerOverlay" class="scrolling-container-overlay"></div>
		<div ref="scrollingContainer" class="scrolling-container" @scroll="handleScroll">
			<slot></slot>
		</div>
		<div v-if="!$isMobile"
			class="scroll-arrow right"
			@mousedown="() => handleArrowClick(360)">
			<BaseIcon
				:isCustom="true"
				icon="backChevron"
				size="3x"/>
		</div>
	</div>
</template>

<script>
	import resize from 'vue-resize-directive';
	import { throttle } from 'lodash';

	export default {
		directives: {
			resize,
		},
		methods: {
			scaleScrollElements({ scrollingContainer, scrollLeft, containerOffset, elementWidth }) {
				const elements = scrollingContainer.querySelectorAll('.scrollable');
				let isLastElement = false;
				let scaler = 1000;
				elements.forEach((element, index) => {
					const useElement = element.querySelector('.missing-image');
					const offset = useElement.offsetLeft;
					const lastElement = (index === elements.length - 1);
					const buffer = (lastElement) ? elementWidth : (elementWidth * 2);
					if (offset - scrollLeft > containerOffset - (buffer)) {
						scaler -= 100;
						const scale = (scaler) / 1000;
						useElement.style.transform = `scale(${scale}) translateX(-${scaler / 100}px)`;
					} else {
						useElement.style.transform = 'scale(1) translateX(0)';
						if (lastElement) {
							isLastElement = true;
						}
					}
				});
				return isLastElement;
			},
			displayScrollOverlay({ scrollingContainer, isLastElement, scrollLeft, elementWidth }) {
				const backgroundColor = 'rgba(255, 255, 255, 0)';
				const hintColor = 'rgba(60,60,60,1)';
				const radiusDefault = '0';
				const radiusOn = '0';
				const threshold = elementWidth * 2;
				let fade = 'none';
				let borderRadius = `${radiusDefault}`;
				if (scrollLeft <= threshold && !isLastElement) {
					// right side
					fade = `linear-gradient(90deg, ${backgroundColor} 0%, ${backgroundColor} 99.5%, ${hintColor} 100%)`;
					borderRadius = `${radiusDefault} ${radiusOn} ${radiusOn} ${radiusDefault}`;
				} else if (scrollLeft > threshold && !isLastElement) {
					// both sides
					fade = `linear-gradient(90deg, ${hintColor} 0%, ${backgroundColor} 0.5%, ${backgroundColor} 99.5%, ${hintColor} 100%)`;
					borderRadius = `${radiusOn}`;
				} else {
					// left side
					fade = `linear-gradient(90deg, ${hintColor} 0%, ${backgroundColor} 0.5%, ${backgroundColor} 100%)`;
					borderRadius = `${radiusOn} ${radiusDefault} ${radiusDefault} ${radiusOn}`;
				}
				const overlay = this.$refs['scrollingContainerOverlay'];
				overlay.style.backgroundImage = fade;
				overlay.style.borderRadius = borderRadius;
			},
			handleResize() {
				const scrollingContainer = this.$refs['scrollingContainer'];
				const scrollLeft = scrollingContainer.scrollLeft;
				const containerOffset = scrollingContainer.clientWidth;
				const lastElementContainer = scrollingContainer.querySelector('.scrollable:last-of-type');
				const lastElement = lastElementContainer.querySelector('.missing-image');
				if (!lastElement) {
					return;
				}
				const elementWidth = lastElement.clientWidth / 2;
				const lastOffset = lastElement.offsetLeft + elementWidth;
				if (lastOffset <= containerOffset) {
					scrollingContainer.style.backgroundImage = 'none';
					return;
				}
				let isLastElement = this.scaleScrollElements({
					scrollingContainer,
					scrollLeft,
					containerOffset,
					elementWidth,
				});
				this.displayScrollOverlay({
					scrollingContainer,
					isLastElement,
					scrollLeft,
					elementWidth
				});
			},
			handleArrowClick(scroll) {
				const scrollingContainer = this.$refs['scrollingContainer'];
				scrollingContainer.scrollBy({
					left: scroll,
					top: 0,
					behavior: 'smooth'
				});
			},
			handleScroll() {
				throttle(this.handleResize, 16)();
			},
		},
		mounted() {
			this.$el.addEventListener('scroll', this.handleScroll);
		},
		destroyed() {
			this.$el.removeEventListener('scroll', this.handleScroll);
		},
	};
</script>

<style lang="scss" scoped>
	@import '@style-vars';
	@import '@style-menu-vars';

	.scrolling-container {
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
		align-content: baseline;
		border-radius: 6px;
		margin: 5px 10px;

		max-height: $scroll_container_height;

		overflow-x: scroll;
		overflow-y: hidden;
		transition: all .5s;
		.scrollable {
			transition: all .1s;
		}
	}
	.scrolling-container-overlay {
		position: absolute;
		top: 0;
		left: 0;
		border-radius: 0px;
		margin-left: 10px;
		width: calc(100% - 20px);
		height: 100%;

		pointer-events: none;
		z-index: 1;
	}
	.scrolling-wrapper {
		.scroll-arrow {
			cursor: pointer;
			position: absolute;
			top: 0;
			width: 0;
			height: 0;
			color: $menu_choice_name_color;
			margin: 20px -5px;
			transition: opacity 300ms, left 300ms, right 300ms, transform 200ms;
			opacity: 0;
			z-index: $zTopInteractionLayer;
		}
		.scroll-arrow.left {
			left: -20px;
		}
		.scroll-arrow.right {
			right: -20px;
			transform: scaleX(-1);
		}
		&:hover {
			.scroll-arrow {
				opacity: 0.75;
				&:hover {
					opacity: 0.8;
				}
				&:active {
					opacity: 1;
				}
			}
			.left {
				left: 0;
				right: unset;
				&:hover {
					transform: scale(1.1);
				}
			}
			.right {
				left: unset;
				right: 0;
				&:hover {
					transform: scale(-1.1, 1.1);
				}
			}
		}
	}
	@include mobile {
		.scrolling-container {
			-webkit-overflow-scrolling: touch;
		}
	}
	@include desktop {}
	@include tablet {}
</style>


// WEBPACK FOOTER //
// src/components/common/BaseScrollingContainer.vue